// Breathing room left between the bottom of the notification and the bottom of
// the viewport, and the floor below which capping the height stops helping.
const CART_NOTIFICATION_BOTTOM_GAP = 10;
const CART_NOTIFICATION_MIN_HEIGHT = 160;
// Applied in this order while the pinned header and links do not fit
// (fitPinnedParts, component-cart-notification.css).
const CART_NOTIFICATION_TIGHT_CLASSES = [
  'cart-notification--tight',
  'cart-notification--tighter',
  'cart-notification--tightest',
];
// Scrolling on past its end closes the notification: a new swipe, or a new turn
// of the wheel, that goes on down this many pixels once the box has rested at
// its end (or had nothing to scroll) for a pause. The gesture that brings the
// box to its end, inertia included, never does. The browser scrolls before
// passive listeners hear of a gesture, so the box's end is timed from its own
// scroll events. A wheel or a trackpad sends a stream of events: a pause
// starts a new gesture. Nothing closes while the box is still sliding in.
// A box that scrolls ends with an empty stretch (updateEndSpace), so its end
// lies past the last of its content.
const CART_NOTIFICATION_SWIPE_CLOSE = 48;
const CART_NOTIFICATION_WHEEL_CLOSE = 60;
const CART_NOTIFICATION_SCROLL_PAUSE = 250;
const CART_NOTIFICATION_CLOSE_DELAY = 500;

// The Consentmo cookie banner (an app embed, not theme code) stays at the
// bottom of the screen until the visitor answers it, and on a phone it covers
// 40 to 55 percent of the viewport: nothing under it can be tapped. It is drawn
// in the open shadow root of <csm-cookie-consent>, a zero-size popover host, so
// the host's own box says nothing; .csm-cookie-box is what is on screen and
// takes the taps, transparent margins included. Consentmo draws it a moment
// after the page loads (on a slow phone, after the visitor may already have
// added to cart) and removes it once the visitor answers.
// It lives here because this file is loaded on every page, with the header.
const cookieBanner = {
  box: null,
  inset: null,
  resizeObserver: null,

  getBox() {
    return document.querySelector('csm-cookie-consent')?.shadowRoot?.querySelector('.csm-cookie-box') || null;
  },

  // The banner's box in viewport coordinates, or null when it is not showing.
  getRect() {
    const box = this.getBox();
    if (!box) return null;
    const rect = box.getBoundingClientRect();
    return rect.width && rect.height ? rect : null;
  },

  // Called whenever the banner may have appeared, gone or changed size. Keeps
  // --cookie-banner-inset on <body> at the height the banner covers
  // (cookie-banner.css turns it into bottom padding on phones, so the end of
  // any page can be scrolled above it) and, on a change, fires
  // cookie-banner:change for an open cart notification to measure itself again.
  update() {
    const box = this.getBox();
    if (box !== this.box) {
      this.box = box;
      this.resizeObserver.disconnect();
      if (box) this.resizeObserver.observe(box);
    }
    const rect = this.getRect();
    const inset = rect ? `${Math.round(rect.height)}px` : null;
    if (inset === this.inset) return;
    this.inset = inset;
    if (inset) document.body.style.setProperty('--cookie-banner-inset', inset);
    else document.body.style.removeProperty('--cookie-banner-inset');
    document.dispatchEvent(new CustomEvent('cookie-banner:change'));
  },

  // Watch the shadow root for the box coming and going, and the box for its
  // size. Consentmo's script may run after this one: until the host shows up
  // the body's children are watched instead, and a host its script has not
  // upgraded yet gets its shadow root when the element is defined.
  watch() {
    const host = document.querySelector('csm-cookie-consent');
    if (host?.shadowRoot) {
      this.resizeObserver = new ResizeObserver(() => this.update());
      new MutationObserver(() => this.update()).observe(host.shadowRoot, { childList: true, subtree: true });
      this.update();
    } else if (host) {
      customElements.whenDefined('csm-cookie-consent').then(() => host.shadowRoot && this.watch());
    } else {
      const bodyObserver = new MutationObserver(() => {
        if (!document.querySelector('csm-cookie-consent')) return;
        bodyObserver.disconnect();
        this.watch();
      });
      bodyObserver.observe(document.body, { childList: true });
    }
  },
};

cookieBanner.watch();

class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onViewportChange = this.setMaxHeight.bind(this);
    // The header itself resizes while the notification is open (the logo grows
    // back when the page returns to the top), which moves the notification down.
    this.headerResizeObserver = new ResizeObserver(this.onViewportChange);
    this.onScroll = this.handleScroll.bind(this);
    this.onTouchStart = this.handleTouchStart.bind(this);
    this.onTouchMove = this.handleTouchMove.bind(this);
    this.onWheel = this.handleWheel.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    // Closed by scrolling past its end, it would open again at its end.
    this.notification.scrollTop = 0;
    this.endAt = null;
    this.setMaxHeight();
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.setMaxHeight();
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
    window.addEventListener('resize', this.onViewportChange);
    window.addEventListener('scroll', this.onViewportChange, { passive: true });
    if (window.visualViewport) window.visualViewport.addEventListener('resize', this.onViewportChange);
    // The cookie banner can show up, or go, while the notification is open.
    document.addEventListener('cookie-banner:change', this.onViewportChange);
    const headerElement = this.header || document.querySelector('.section-header');
    if (headerElement) this.headerResizeObserver.observe(headerElement);
    // Scrolling on past the end closes it (the constants at the top).
    this.openedAt = performance.now();
    this.swipe = null;
    this.wheel = null;
    this.wheelAt = -Infinity;
    this.notification.addEventListener('scroll', this.onScroll, { passive: true });
    this.notification.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.notification.addEventListener('touchmove', this.onTouchMove, { passive: true });
    this.notification.addEventListener('wheel', this.onWheel, { passive: true });

    this.dispatchCartViewEvent();
  }

  // The notification is anchored to the sticky header, so scrolling the page
  // never brings its bottom into view: whatever falls below the fold stays
  // unreachable. Measure the space actually left under the header and cap the
  // box there — component-cart-notification.css makes the overflow scroll
  // inside. Recomputed on scroll, viewport resize and header resize, because all
  // three move the top edge (the announcement bar scrolls away, the logo shrinks
  // and grows back).
  setMaxHeight() {
    const wrapper = this.notification.parentElement;
    if (!wrapper) return;

    // visualViewport is what is really on screen: on mobile Chrome the layout
    // viewport (innerHeight) stays tall while the URL bar is still showing.
    let bottom = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    let minHeight = CART_NOTIFICATION_MIN_HEIGHT;

    // A cookie banner across the notification's column (phones; on desktop it
    // sits in the other corner) is the real bottom edge. No floor then: below
    // the banner's top edge the buttons could be seen but not tapped.
    const banner = cookieBanner.getRect();
    const box = this.notification.getBoundingClientRect();
    if (banner && banner.left < box.right && banner.right > box.left && banner.top < bottom) {
      bottom = banner.top;
      minHeight = 0;
    }

    const available = bottom - wrapper.getBoundingClientRect().top - CART_NOTIFICATION_BOTTOM_GAP;
    this.notification.style.setProperty(
      '--cart-notification-max-height',
      `${Math.max(Math.round(available), minHeight)}px`
    );
    this.fitPinnedParts();
    this.updateEndSpace();
    // A new height can move the end of the box without scrolling it.
    this.handleScroll();
  }

  // Only a box that scrolls gets the empty stretch after the rewards
  // (component-cart-notification.css), which is left out of the measure.
  updateEndSpace() {
    const scrolls = this.notification.classList.contains('cart-notification--scrolls');
    const rewards = this.notification.querySelector('#cart-notification-freeshipping');
    const endSpace = scrolls && rewards ? parseFloat(getComputedStyle(rewards, '::after').height) || 0 : 0;
    this.notification.classList.toggle(
      'cart-notification--scrolls',
      this.notification.scrollHeight - endSpace > this.notification.clientHeight + 1
    );
  }

  // The header, with the close button, and the links stay pinned at the top and
  // at the bottom of the box while the product and the rewards scroll between
  // them (component-cart-notification.css). When the box cannot hold both, part
  // of the bottom padding goes first, then "Visualizza carrello"; last, checkout
  // and the close button share a single row.
  fitPinnedParts() {
    const header = this.notification.querySelector('.cart-notification__header');
    const links = this.notification.querySelector('.cart-notification__links');
    this.notification.classList.remove(...CART_NOTIFICATION_TIGHT_CLASSES);
    // The top margin of the links collapses outside their box, but it is
    // pinned along with them: the cover in the CSS spans it. Read with no
    // tight class on, as the single row has no such margin.
    const linksGap = parseFloat(getComputedStyle(links.firstElementChild).marginTop);
    const fits = () => {
      const { paddingTop, paddingBottom } = getComputedStyle(this.notification);
      const pinnedTop = parseFloat(paddingTop) + header.offsetHeight;
      const pinnedBottom = linksGap + links.offsetHeight + parseFloat(paddingBottom);
      // Keyboard focus scrolls what it lands on into the space between the two.
      this.notification.style.scrollPaddingTop = `${pinnedTop}px`;
      this.notification.style.scrollPaddingBottom = `${pinnedBottom}px`;
      return pinnedTop + pinnedBottom <= this.notification.clientHeight;
    };

    for (const tightClass of CART_NOTIFICATION_TIGHT_CLASSES) {
      if (fits()) return;
      this.notification.classList.add(tightClass);
    }
    // On a single row nothing is left to scroll behind it.
    this.notification.style.scrollPaddingTop = '';
    this.notification.style.scrollPaddingBottom = '';
  }

  // Since when the box has been at its end (the constants at the top).
  handleScroll() {
    const { scrollTop, scrollHeight, clientHeight } = this.notification;
    if (scrollTop + clientHeight < scrollHeight - 1) this.endAt = null;
    else if (this.endAt === null) this.endAt = performance.now();
  }

  canCloseOnScroll() {
    const now = performance.now();
    return (
      now - this.openedAt >= CART_NOTIFICATION_CLOSE_DELAY &&
      this.endAt !== null &&
      now - this.endAt >= CART_NOTIFICATION_SCROLL_PAUSE
    );
  }

  handleTouchStart(evt) {
    const touch = evt.touches.length === 1 && this.canCloseOnScroll() ? evt.touches[0] : null;
    this.swipe = touch && { x: touch.clientX, y: touch.clientY };
  }

  // Decided once the finger has gone far enough: up, which scrolls the content
  // down, closes; down or sideways never does.
  handleTouchMove(evt) {
    if (!this.swipe) return;
    const touch = evt.touches[0];
    const down = this.swipe.y - touch.clientY;
    const across = Math.abs(touch.clientX - this.swipe.x);
    if (Math.max(Math.abs(down), across) < CART_NOTIFICATION_SWIPE_CLOSE) return;
    this.swipe = null;
    if (down > across) this.close();
  }

  handleWheel(evt) {
    const now = performance.now();
    if (now - this.wheelAt > CART_NOTIFICATION_SCROLL_PAUSE) this.wheel = this.canCloseOnScroll() ? 0 : null;
    this.wheelAt = now;
    if (this.wheel === null || Math.abs(evt.deltaX) > Math.abs(evt.deltaY)) return;
    // Firefox with a mouse counts in lines.
    this.wheel += evt.deltaMode === WheelEvent.DOM_DELTA_LINE ? evt.deltaY * 40 : evt.deltaY;
    if (this.wheel >= CART_NOTIFICATION_WHEEL_CLOSE) this.close();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);
    window.removeEventListener('resize', this.onViewportChange);
    window.removeEventListener('scroll', this.onViewportChange);
    if (window.visualViewport) window.visualViewport.removeEventListener('resize', this.onViewportChange);
    document.removeEventListener('cookie-banner:change', this.onViewportChange);
    this.headerResizeObserver.disconnect();
    this.notification.removeEventListener('scroll', this.onScroll);
    this.notification.removeEventListener('touchstart', this.onTouchStart);
    this.notification.removeEventListener('touchmove', this.onTouchMove);
    this.notification.removeEventListener('wheel', this.onWheel);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-notification-freeshipping',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
